import React, { lazy, Suspense, useEffect, useReducer, useState } from 'react'

import { pathOr } from 'ramda'
import { connect, useSelector } from 'react-redux'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'

import { generateContactsWebAppConfig } from '@driftt/app-config'
import {
  Flex,
  ThemeProvider as DDSThemeProvider,
  ToastProvider,
} from '@driftt/dds'
import { DriftAnalytics } from '@driftt/dds.analytics'
import { DriftUser } from '@driftt/dds.analytics/dist/packages/analytics/lib/types/user'
import { Div } from '@driftt/tide-core'
import { TrafficControlRootState } from '@driftt/traffic-control'

import { fetchAudiences } from 'api/customAudiences'
import { fetchPropertyDefinitions } from 'api/propertyDefinitions'
import { PropertyDefinition } from 'api/propertyDefinitions/types'
import { AudiencesBanner } from 'components/AudiencesBanner'
import Subnav from 'components/Subnav'
import config from 'config'
import { PropertyDefinitionContext } from 'contexts'
import CustomAudiencesContext from 'modules/CustomAudiences/context'
import customAudiencesReducer, {
  INITIAL_STATE,
} from 'modules/CustomAudiences/reducer'
import RootState from 'types/rootState'
import { hasGates } from 'utils/access'

import './style.css'
import './customAudienceModal.css'
const AudienceContainer = lazy(() => import('components/AudienceContainer'))
const AudienceList = lazy(() => import('components/AudienceList'))
const AudienceDetailLayout = lazy(() => import('layouts/AudienceDetail'))

const AppLayout = ({ appConfig }: any) => {
  const isBot = useSelector(pathOr(false, ['user', 'bot']))
  const [customAudiencesState, customAudiencesDispatch] = useReducer(
    customAudiencesReducer,
    INITIAL_STATE
  )
  const driftUser = useSelector<TrafficControlRootState, DriftUser>(
    (state) => state.user
  )

  const [propertyDefinitions, setPropertyDefinitions] = useState<
    PropertyDefinition[]
  >([])

  useEffect(() => {
    const doFetchAudiences = async () => {
      try {
        const { data } = await fetchAudiences()
        customAudiencesDispatch({
          type: 'REPLACE_AUDIENCES',
          payload: data.audiences,
        })
      } finally {
        customAudiencesDispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    doFetchAudiences()
  }, [])

  useEffect(() => {
    fetchPropertyDefinitions().then(({ data: { results } }) => {
      setPropertyDefinitions(results)
    })
  }, [])

  useEffect(() => {
    driftUser && DriftAnalytics.initialize({ user: driftUser })
  }, [driftUser])

  const userHasAudiences = pathOr(false, ['hasAudiences'], appConfig)

  const accessible = userHasAudiences || hasGates('has_demo_org') || isBot
  return (
    <Router basename="/audiences">
      <Div className="audience-layout-container">
        <PropertyDefinitionContext.Provider value={propertyDefinitions}>
          <CustomAudiencesContext.Provider
            value={{
              state: customAudiencesState,
              dispatch: customAudiencesDispatch,
            }}
          >
            <DDSThemeProvider>
              <ToastProvider>
                {accessible ? (
                  <>
                    <Subnav appConfig={appConfig} />
                    <Flex direction="column" fullWidth fullHeight>
                      <AudiencesBanner />
                      <Switch>
                        <Route
                          path="/details"
                          render={() => (
                            <Suspense fallback={<div />}>
                              <AudienceDetailLayout />
                            </Suspense>
                          )}
                        />
                        <Route
                          path="/:id"
                          render={() => (
                            <Suspense fallback={<div />}>
                              <AudienceContainer />
                            </Suspense>
                          )}
                        />
                        <Route path="/">
                          <Suspense fallback={<div />}>
                            <AudienceList />
                          </Suspense>
                        </Route>
                      </Switch>
                    </Flex>
                  </>
                ) : (
                  (window.location.href = config.CONTACTS_URL)
                )}
              </ToastProvider>
            </DDSThemeProvider>
          </CustomAudiencesContext.Provider>
        </PropertyDefinitionContext.Provider>
      </Div>
    </Router>
  )
}

const mapStateToProps = (state: RootState) => ({
  appConfig: generateContactsWebAppConfig(state.user),
})

export default connect(mapStateToProps)(AppLayout)
